<script setup lang="ts">
import { SplideSlide } from '@splidejs/vue-splide'

const { tags, items, fakeItems, blogUuid } = defineProps<{
  blogUuid?: string
  title: StoryBlokHeading
  tags?: string[]
  items?: string[]
  showAll?: string
  fakeItems?: {
    image: StoryBlokImage
    publishDate: string
    title: string
    perex: StoryBlokRichText
    authorName: string
    url: StoryBlokLink
  }[]
  showAllUrl?: StoryBlokLink
}>()

const config = useRuntimeConfig()
const blogUrl = config.public.blog.url
const articles = ref([] as StoryBlokArticle[])
const blogApi = useStoryblokBlog()

if (fakeItems?.length) {
  articles.value = fakeItems.map(item => ({ content: item }))
}
else if (items?.length) {
  const articlesByUuids = await blogApi.getArticles(1, undefined, { by_uuids: items.join(',') })
  if (articlesByUuids)
    articles.value = articlesByUuids
}
else if (tags?.length) {
  const articlesByTags = await blogApi.getArticles(1, { tags: { in_array: tags.join(',') } })
  if (articlesByTags)
    articles.value = articlesByTags.filter(article => article.uuid !== blogUuid)
}
const sliderOptions = {
  type: 'loop',
  fixedWidth: '373px',
  gap: '20px',
  perPage: 3,
  breakpoints: {
    1160: {
      perPage: 2,
    },
    768: {
      perPage: 2,
      fixedWidth: '315px',
    },
    640: {
      perPage: 1,
      padding: '30px',
      fixedWidth: '315px',
      gap: '10px',
    },
  },
}
</script>

<template>
  <div class="w-full">
    <StoryblokHeading class="mx-5 sm:text-center" :title="title" />
    <div class="mt-6 <sm:-mx-5">
      <AppSlider :options="sliderOptions">
        <SplideSlide v-for="article in articles" class="article-card">
          <StoryblokLink :to="article?.content?.url?.cached_url || `${blogUrl}/${article.slug}`">
            <StoryblokImage class="rounded-t-xl" :src="article.content?.image" width="373" mwidth="315" height="190" mheight="190" />
            <div class="flex flex-wrap absolute w-full top-0 left-0 gap-2 p-5">
              <StoryblokLink v-for="tag in article.content?.tags" :to="`${blogUrl}/tag/${tag.slug}`">
                <span class="tag-success">{{ tag.name }}</span>
              </StoryblokLink>
            </div>
            <div class="py-5 md:h-212px overflow-hidden">
              <div class="px-3">
                <span class="whitespace-nowrap">{{ useFormatDate(article.content?.publishDate, 'dd. MMMM yyyy') }}</span>
                <span class="px-2">|</span>
                <span v-if="article?.content?.authorName">{{ article?.content?.authorName }}</span>
                <span v-for="(author, i) in article?.content?.authors" v-else class="underline-gray-dark hover:underline">
                  <span v-if="i">,&nbsp;</span>
                  <StoryblokLink :to="`${blogUrl}/autor/${author.slug}`">{{ author.name }}</StoryblokLink>
                </span>
              </div>
              <div class="px-3 h-full overflow-hidden group cursor-pointer">
                <div class="pt-10px line-clamp-2 underline-gray-dark group-hover:underline headline-3">
                  {{ article?.content.title }}
                </div>
                <div class="pt-2.5">
                  <StoryblokRichText v-if="article.content?.perex" class="line-clamp-4 sm:line-clamp-3" :document="article.content.perex" />
                </div>
              </div>
            </div>
          </StoryblokLink>
        </SplideSlide>
      </AppSlider>
    </div>
    <div class="grid place-items-center mt-6">
      <StoryblokButton icon-end="fas-arrow-right" icon-size="small" render="link" class="font-semibold">
        <StoryblokLink :to="`${showAllUrl?.cached_url || blogUrl}`">
          {{ showAll }}
        </StoryblokLink>
      </StoryblokButton>
    </div>
  </div>
</template>

<style scoped>
.article-card {
  @apply h-436px relative rounded-xl box-border bg-white flex flex-col border border-gray-light
         md:h-422px md:max-w-full;
}
</style>
